// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import HomeAccordion from "@keller/App/Components/HomePage/HomeAccordion";
import { getNotifyStyle } from "@keller/App/Components/Notify";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

// XNG-Changes: add numberOfRecords
const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
    noQuotesText: {
        ...getNotifyStyle("info"),
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            recentQuotesParameter.pageSize = this.props.fields.numberOfRecords || recentQuotesParameter.pageSize;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        // XNG-Changes: change structure to Table
        if (quotesDataView.value.length === 0) {
            return null;
        }

        return (
            <HomeAccordion title={translate("Quote Requests")} link={myQuotesPageUrl} linkText={translate("View All")}>
                {quotesDataView.value.length === 0 && (
                    <GridItem {...styles.noQuotesGridItem}>
                        <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                    </GridItem>
                )}
                {quotesDataView.value.length > 0 && (
                    <GridItem width={12}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("َQuote #")}</DataTableHeader>
                                {settingsCollection.quoteSettings.jobQuoteEnabled && (
                                    <>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("َType")}
                                        </DataTableHeader>
                                    </>
                                )}
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Status")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Requested")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Expires")}</DataTableHeader>
                                {session.isSalesPerson && (
                                    <>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("Sales Rep")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("User")}
                                        </DataTableHeader>
                                    </>
                                )}
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Customer")}</DataTableHeader>
                                {!session.isSalesPerson && (
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Ship To / Pick Up")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {quotesDataView.value.map(
                                    quote =>
                                        quote !== null && (
                                            <DataTableRow
                                                {...styles.dataTableRow}
                                                data-test-selector="recentQuotes_dataLine"
                                                key={quote.id}
                                            >
                                                <QuoteSummaryCard
                                                    quote={quote}
                                                    session={session}
                                                    quoteSettings={settingsCollection.quoteSettings}
                                                    extendedStyles={styles.quoteSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </HomeAccordion>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
